import { useNavigate, useParams } from 'react-router-dom';
import { FormatListBulleted } from '@mui/icons-material';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import { useListResourceTypesQuery } from '../../store/hooks';
import {
  parseResourceScope,
  resourceListPath,
} from '../../utils/resource-routes';

interface ResourceCapability {
  org?: boolean;
  user?: boolean;
  patient?: boolean;
}

interface ResourceTypeRow {
  resource_type: string;
  model?: string;
  scope?: string;
  capability?: ResourceCapability | null;
}

/**
 * Catalog of registered resource types for one scope (GDN-2723).
 *
 * At user scope the catalog only offers types whose canonical `list` accepts a
 * `user_id` — the rest have no user-scoped answer to give, so linking to them
 * would just produce a 400 `dimension_not_supported`.
 */
export default function ResourceCatalogPage() {
  const params = useParams<{ organizationId?: string; userId?: string }>();
  const navigate = useNavigate();
  const scope = parseResourceScope(params);
  const { data, isLoading } = useListResourceTypesQuery();

  if (!scope) return null;

  const all = (data?.items ?? []) as ResourceTypeRow[];
  const resources = scope.userId ? all.filter(r => r.capability?.user) : all;

  return (
    <Card>
      <CardHeader
        title="Resources"
        subheader={
          scope.userId
            ? 'Every registered resource type that can be listed for this user.'
            : 'Every registered resource type in this organization. Types without a listable dimension can still be opened by id from a detail link.'
        }
      />
      <CardContent>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Resource type</TableCell>
                <TableCell>Model</TableCell>
                <TableCell>Scope</TableCell>
                <TableCell align="right">Browse</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {resources.map(r => {
                const listable = scope.userId
                  ? r.capability?.user
                  : r.capability?.org;
                return (
                  <TableRow key={r.resource_type} hover>
                    <TableCell>{r.resource_type}</TableCell>
                    <TableCell>{r.model}</TableCell>
                    <TableCell>
                      {r.scope ? <Chip size="small" label={r.scope} /> : '—'}
                    </TableCell>
                    <TableCell align="right">
                      {listable ? (
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<FormatListBulleted />}
                          onClick={() =>
                            navigate(resourceListPath(scope, r.resource_type))
                          }
                        >
                          Browse
                        </Button>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          not listable
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
